/**
 * Comprehensive error filtering for Sentry to reduce noise from known non-critical errors
 */

// Relative rather than the `ui/*` alias: this module sits outside every app's
// tsconfig tree and each app's Sentry config already reaches it by relative
// path, so resolving it this way doesn't depend on any app's `paths` map.
// `page-unloading` must stay import-free for the same reason — web, apollo and
// nutrition pull this file into their client bundles, apollo into its server
// and edge ones too.
import { isPageUnloading } from "../../packages/ui/lib/page-unloading";
import { isFirebaseUnobservedSdkRejection } from "./firebase-rejections";

// Type definitions - compatible with Sentry without requiring specific imports
type Event = any;
type EventHint = any;
type Breadcrumb = any;
type Exception = any;
type StackFrame = any;

// Error patterns to completely filter out (return null).
// Network/abort wordings are duplicated in RESPONSELESS_FAILURE_MESSAGES, which
// is read earlier and off the thrown error rather than the event message. Add a
// new engine wording to both, or `user_visible` events silently stay uncovered.
const FILTERED_ERROR_PATTERNS = [
  // WebKit messageHandlers errors from iOS app
  /window\.webkit\.messageHandlers/i,
  /webkit\.messageHandlers/i,
  
  // PCM Bridge callback handler errors
  /_pcmBridgeCallbackHandler/i,
  
  // iOS app hanging errors
  /app hanging/i,
  /application hang/i,
  
  // Network errors from iOS app
  /NSURLErrorDomain/i,
  /NSURLError/i,
  
  // Generic network failures that are likely transient
  /failed to fetch/i,
  /network request failed/i,
  /networkerror/i,
  /network error/i,
  
  // Next.js navigation errors
  /Invariant: attempted to hard navigate to the same URL/i,
  
  // ResizeObserver errors (usually benign)
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  
  // Non-actionable browser errors
  /Non-Error promise rejection captured/i,
  /Non-Error exception captured/i,
  
  // Script loading errors from extensions or blockers
  /Failed to load script/i,
  /Script error/i,

  // Video autoplay races and permission denials on social-app in-app browsers
  // (Instagram, TikTok) where /meta/* and /tiktok/* landing pages live.
  // The play() request gets interrupted in several flavors — by pause(), by
  // a new load request, by the browser saving power on background tabs.
  // Plus the autoplay-blocked variant. All visible-to-no-one, all dominate
  // event volume.
  /The play\(\) request was interrupted/i,
  /The request is not allowed by the user agent or the platform/i,

  // AbortError from in-flight fetch cancelled on navigation. Common in SPAs
  // when users navigate before a request resolves — not a real failure.
  /The operation was aborted/i,

  // Chrome 100+ wording for the same AbortError: when AbortController.abort()
  // is called without a reason argument, the signal's reason becomes a
  // DOMException with this message. Fires in usePubnubListener cleanup when
  // pubnub.unsubscribe() cancels the in-flight subscribe long-poll request
  // via its internal AbortController — the rejection bubbles as unhandled.
  // LADDER-WEBSITE-8D3, 193 events, 3 users on /app/connect.
  /signal is aborted without reason/i,

  // Axios uses its own wording for cancelled requests, so the JS-native
  // `/The operation was aborted/` above doesn't catch it. Same root cause
  // (navigation cancellation), separate filter. LADDER-WEBSITE-75K,
  // ~1k events on /auth/success since 2025-07.
  /Request aborted/i,

  // A coach declined to leave a page with unsaved changes. Next has no API to
  // cancel a push, so the guard throws from routeChangeStart and Link's push
  // rejects unhandled. Intended, not a failure (apollo save-bar.tsx).
  /Navigation cancelled: unsaved changes/,

  // <video> media element abort — fires when a video is detached or
  // navigated away mid-load. Distinct wording from the JS-native
  // operation-aborted variant. LADDER-WEBSITE-7TV.
  /The fetching process for the media resource was aborted/i,

  // Old Safari / Instagram WebView versions reject modern JS regex syntax
  // (lookbehind, named groups) at parse time. Browser-compat noise.
  /Invalid regular expression: invalid group specifier name/i,

  // <video> source format unsupported by the embedded browser (mostly old
  // Instagram/TikTok WebView on older iOS). Same /tiktok/[slug] +
  // /meta/[slug] surface as the other autoplay noise. LADDER-WEBSITE-828,
  // ~15k events.
  /Failed to load because no supported source was found/i,

  // Same class of <video> source-unsupported noise on landing pages —
  // separate wording from the variant above. LADDER-WEBSITE-7ZZ surfaces
  // this one with ~11.5k lifetime events on /.
  /The element has no supported sources/i,

  // Android WebView teardown: the host activity finishes mid-JS and the
  // bridge tears down its Java side, so any subsequent postMessage /
  // logging call from JS gets `Java object is gone`. Pure WebView
  // lifecycle, not a bug. Covers LADDER-WEBSITE-89J / 8A0 / 89Z.
  /Java object is gone/i,

  // Same Android WebView teardown, the other wording Chromium's Java bridge
  // uses: the bridged method exists but its Java side raised, so the call
  // fails with `Error invoking <method>: Java exception was raised during
  // method invocation` instead of `Java object is gone`. Both of Meta's Android
  // in-app browsers inject `app://navigation_performance_logger_android` —
  // 8KX splits Instagram/Facebook 64/7 over 90d — whose beforeunload
  // handler (sendBeforeUnloadMessage -> sendDataToNative -> postMessage) fires
  // while the host activity is already finishing. Nothing here is ours: the
  // repo has no Android JS bridge at all (`addJavascriptInterface`,
  // `window.Android`, `JSBridge` all grep to zero), so only an injected script
  // can produce this string.
  //
  // A stack-shape filter can't cover it. The same error groups two ways
  // depending on which Sentry instrumentation caught it — LADDER-WEBSITE-8KT
  // via the global onerror handler (pure injected-script frames) and
  // LADDER-WEBSITE-8KX via the wrapped addEventListener, whose stack carries
  // our own `_next/static` chunks *interior* to it. Those frames are the SDK's
  // wrapper, not a first-party caller, so any "no first-party frame" test
  // fails open on 8KX. Both stack shapes are pinned in
  // apps/web/sentry-error-filter.test.ts.
  //
  // Not tied to any one route — it is a beforeunload handler, so it follows the
  // in-app browser rather than the page. Over 90d it spans eight transactions,
  // 8KT weighted to /quiz (130/196) and 8KX to /team/[slug] (54/71).
  //
  // Keyed on Chromium's half of the message rather than the method name, so it
  // covers the whole `Error invoking <method>: …` class. The tradeoff: if a
  // Ladder Android WebView host ever ships its own JS interface, a genuinely
  // broken first-party bridge call goes silent here — the same tradeoff the
  // iOS `window.webkit.messageHandlers` pattern at the top already makes.
  /Java exception was raised during method invocation/i,

  // Radix UI's runtime accessibility check fires as console.error every time
  // a DialogContent renders without a DialogTitle (or VisuallyHidden-wrapped
  // one). Sentry catches the console.error and turns it into an event. The
  // a11y issue is real and worth fixing at the component level, but the
  // capture spam (~1300 events/user) is pure noise. LADDER-WEBSITE-541,
  // ~9.3k events lifetime.
  /DialogContent.*requires.*DialogTitle/i,

  // Radix UI tooltip bug: the scroll handler does
  // `if (target?.contains(context.trigger)) onClose()`, assuming target
  // is a Node. For `scroll` events on `Window`, target IS Window — which
  // doesn't have a .contains() method. The optional chain doesn't help
  // because the property exists as undefined, not absent. Lives in
  // @radix-ui/react-tooltip and we can't fix it from our code without
  // upgrading. LADDER-WEBSITE-5XK, ~1.7k events lifetime, not user-visible
  // (tooltip just doesn't close cleanly on scroll).
  /t\?\.contains is not a function/,

  // Facebook in-app browser injects its own JS into pages it loads
  // (variables like EmptyRanges and minified Kf/vH/w/Ob referenced from
  // undefined:LINE:COL with no real stack). When the IAB version updates
  // and their injected script breaks, every page load fires an error that
  // bubbles to window.onerror. Not our code, can't fix. LADDER-WEBSITE-5J0
  // alone is ~30k events lifetime, all browser=Facebook.
  /Can't find variable: EmptyRanges/,

  // Third-party tracking-pixel globals referenced by GTM tags when the
  // pixel script itself was blocked or never loaded. `twq` is the
  // X/Twitter pixel, `_satellite` is Adobe Launch — neither is loaded
  // first-party anywhere in this repo (grep returns only this file), so
  // a bare message filter is safe. These already fall under the TikTok
  // UA + analytics-frame conjunction filter below when the stack
  // cooperates, but bursts arrive with truncated stacks or from non-TikTok
  // in-app browsers and slip through: LADDER-WEBSITE-8NZ / 8P0 (twq,
  // ~5.9k users in one 2026-07 burst) and 8CE / 8CF (_satellite).
  /twq is not defined/i,
  /Can't find variable: twq/i,
  /_satellite is not defined/i,
  /Can't find variable: _satellite/i,

  // iOS WKWebView bridge: a postMessage from page JS (usually an injected
  // tag or the host app's own bridge) gets no reply because the native
  // handler is gone — fires when the webview is being torn down or the
  // host app killed the handler. Sibling of the `Java object is gone`
  // Android filter above. LADDER-WEBSITE-89X, ~80 users/month on
  // /tiktok/[slug] landing pages, invisible to the user.
  /WKWebView API client did not respond to this postMessage/i,

  // MetaMask's browser extension injects a provider script into every page and
  // has it connect back to the extension process. When the extension is
  // disabled, updating, or its content script is orphaned, that connect fails —
  // `Failed to connect to MetaMask`, with `MetaMask extension not found` linked
  // as its cause. Desktop Chrome, follows the browser rather than the page (12
  // transactions over `/`, `/app`, `/quiz`, `/pricing`, …). LADDER-WEBSITE-89K,
  // 190 events / 57 users since 2026-05.
  //
  // The only frame is `app:///scripts/inpage.js` — the extension's own script;
  // `app:///` is just what rewriteFrames prefixes onto a same-origin-looking
  // path, not evidence we served it (this repo serves only `hls.min.js` and
  // `af-smart-script.js` out of `public/scripts/`). Safe as a bare message
  // pattern because there is no wallet surface to mask: `MetaMask`, `inpage`,
  // `ethereum`, `web3`, `wagmi` and `walletconnect` all grep to zero across
  // every app and package. Keyed on the vendor name alone so it covers both
  // halves of the linked-error chain, whichever one `getErrorMessage` reaches
  // first.
  /MetaMask/i,

  // Brave on iOS injects a wallet shim into every page that runs
  // `window.ethereum.selectedAddress = undefined`; with the wallet off,
  // `window.ethereum` is undefined and the assignment throws. The only frame
  // is `app:///<page>:1:16 (global code)`, the injected script attributed to
  // the page URL. Safe as a bare pattern for the same no-wallet-surface reason
  // as MetaMask above. LADDER-WEBSITE-8WM / 8XV.
  /window\.ethereum/i,

  // Facebook's and Instagram's iOS in-app browsers inject a script that reads
  // `window._META.IABInnerFrame` before the IAB has defined `_META`. Same shape
  // as the Brave shim above: one `app:///<page>:1:136 (global code)` frame.
  // Safe as a bare pattern because `_META` and `IABInnerFrame` grep to zero
  // outside this file. LADDER-WEBSITE-8ZP and its per-page siblings.
  /IABInnerFrame/,
];

// Stack-frame function names that identify errors thrown inside JS injected
// into our pages by someone else — an in-app browser's bridge, or a tag
// manager's vendor script — rather than by our code. Matched against
// `frame.function` anywhere in the exception's stacktrace because the
// error message itself ("Cannot read properties of undefined (reading
// 'value')") is too generic to filter on safely — we'd catch real bugs.
const INJECTED_BRIDGE_FUNCTIONS = [
  // Instagram IAB contact autofill bridge. Browser tag is "Instagram",
  // OS Android, stack is all `<anonymous>:37:...` with these function
  // names. Covers LADDER-WEBSITE-8A4 / 7R5 (TypeError: Cannot read
  // properties of undefined (reading 'value')).
  "setContactAutofillValuesFromBridge",
  "execute_autofill_callback",

  // Facebook IAB's own login-form-focus bridge on /quiz: it calls
  // postMessage with a target/origin the WebView rejects, throwing
  // `InvalidAccessError: The object does not support the operation or
  // argument.` (DOMException code 15). Browser tag is "Facebook", stack is
  // fully minified (`app:///quiz:1:...`) but these function names are
  // unique to the bridge's own call chain
  // (logLoginFormFocused -> sendPostMessage -> dispatchToBridge ->
  // postMessage) — grepping the repo for them returns zero matches, so
  // they can't collide with a first-party frame. Covers LADDER-WEBSITE-8GZ,
  // 1k+ events / 58 users and escalating.
  "logLoginFormFocused",
  "dispatchToBridge",

  // Curalate / Bazaarvoice social-commerce tag. An injected inline script
  // polls on a setInterval for the vendor SDK to appear and then reads
  // `crl8.trackers`. In TikTok's in-app browser the SDK script never loads,
  // so `crl8` stays undefined and EVERY tick throws — several events per
  // user per second, which is why 717 users produced 2,482 events in the
  // first 15 hours (LADDER-WEBSITE-8QK, escalating).
  //
  // The TikTok filter below doesn't catch it: `isTikTokAnalyticsError`
  // requires a frame path matching a known analytics bundle, and this stack
  // has only two frames — our chunk's Sentry setInterval wrapper, and
  // `<anonymous>` for the injected script. No bundle path, no match. The
  // message is a bare "Cannot read properties of undefined (reading
  // 'trackers')", far too generic to regex on.
  //
  // The function name is the reliable signal: `crl8` / `checkCrl8Ready` /
  // `curalate` appear nowhere in this repo (the lone grep hit is base64
  // inside a rewards SVG), so this cannot mask a first-party bug. Fixing
  // the poller itself is a tag-manager change, not a repo one — see
  // LADD-4368.
  "checkCrl8Ready",
];

// Error patterns to log but not send to Sentry
const LOGGED_ERROR_PATTERNS = [
  // CSS chunk loading failures
  /Loading CSS chunk/i,
  /Failed to load CSS/i,
  /ChunkLoadError.*\.css/i,
  
  // JS chunk loading failures (might indicate deployment issues)
  /Loading chunk/i,
  /ChunkLoadError/i,

  // Safari/WebKit's wording for the same chunk-load failure as ChunkLoadError
  // above — a dynamic import() of an ES module chunk fails with no stacktrace
  // attached (WebKit doesn't provide one), so it never matched the
  // ChunkLoadError/Loading-chunk patterns and slipped through as a full
  // Sentry report instead of being logged like its Chrome/webpack sibling.
  // LADDER-WEBSITE-58Z, 238 events / 187 users, mostly /auth/success,
  // ongoing since 2024-11.
  /Importing a module script failed/i,
];

/**
 * Check if an error message matches any of the filtered patterns
 */
function matchesFilteredPattern(message: string | undefined): boolean {
  if (!message) return false;
  return FILTERED_ERROR_PATTERNS.some(pattern => pattern.test(message));
}

/**
 * Check if the exception's stack contains a frame whose function name
 * matches a known IAB-injected bridge function. Used to filter errors
 * whose message is too generic to regex on but whose stack uniquely
 * identifies them as third-party noise.
 */
function isInjectedBridgeError(event: Event): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames || !Array.isArray(frames)) return false;
  return frames.some((frame: StackFrame) => {
    const fn = frame.function ?? "";
    return INJECTED_BRIDGE_FUNCTIONS.some((name) => fn.includes(name));
  });
}

/**
 * Facebook / Instagram in-app browsers inject their own JavaScript into
 * pages they load (login bridge, contact autofill, analytics shims).
 * When the IAB updates and its injected script references a variable
 * that no longer exists, every interaction with the page fires
 * `ReferenceError: Can't find variable: <something>`.
 *
 * The variable names vary wildly by IAB version — we've seen
 * EmptyRanges, Kf, vH, w, Ob, Cd, M, a8, ci — too unpredictable to
 * maintain as message-pattern regexes. The reliable signal is the browser:
 * only Facebook / Instagram IABs produce these. Real Chrome/Safari users
 * hitting genuine ReferenceErrors are NOT caught (different UA).
 *
 * Detected from the live `navigator.userAgent`, NOT `event.contexts.browser`:
 * that context is derived at Sentry ingest from the UA header and does not
 * exist client-side in beforeSend — guarding on it makes the filter a silent
 * no-op (same defect fixed for the Chrome-iOS filter below; see LADD-3638).
 * `FBAN`/`FBAV` are Facebook's app UA tokens, `Instagram` is Instagram's own.
 *
 * Covers LADDER-WEBSITE-5J0 (EmptyRanges, Facebook IAB) and the
 * 8BR / 8BS / 8BT / 8BV family (M / a8 / ci on /meta/[slug],
 * Instagram IAB).
 */
function isIabInjectedReferenceError(event: Event): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isFacebookOrInstagram =
    ua.includes("FBAN") || ua.includes("FBAV") || ua.includes("Instagram");
  if (!isFacebookOrInstagram) return false;
  const value = event.exception?.values?.[0]?.value ?? "";
  return /^Can't find variable: /.test(value);
}

/**
 * Script paths that mark a stack frame as third-party Google Tag Manager /
 * analytics code (GTM, Segment's GTM integration bundle, Google Optimize)
 * rather than our own. These load on the /tiktok/* and /meta/* landing pages.
 */
const ANALYTICS_SCRIPT_FRAME =
  /gtm\.js|google-tag-manager|optimize\.js|integrations\/vendor\/commons/i;

/**
 * TikTok's in-app browser runs our landing pages in a restricted WebView where
 * GTM / Segment / Optimize initialization routinely throws: `consentManager`
 * and `consent` are undefined, a tag assigns `.event` on an undefined global,
 * a tag calls `addEventListener` on a missing element, etc.
 *
 * The messages are generic TypeErrors — too risky to regex on, since the same
 * wording describes real first-party bugs. The reliable signal (mirroring the
 * IAB filters above) is a conjunction: the live UA is TikTok's in-app browser
 * AND the error originates inside the GTM/analytics bundle. These fire via the
 * global onerror handler with no first-party frames, so real Chrome/Safari
 * users and genuine app errors are not caught.
 *
 * Detected from `navigator.userAgent`, NOT `event.contexts.browser`:
 * that context is derived at Sentry ingest from the UA header and does not
 * exist client-side in beforeSend — guarding on it makes the filter a silent
 * no-op (see LADD-3638).
 *
 * UA tokens (LADD-3817): modern TikTok iOS WebView UAs carry NO "bytedance"
 * token — the real strings (pulled from our own Sentry trace data, TikTok
 * 45.7–46.1 on iOS) look like
 * `... Safari/604.1 musical_ly_46.0.0 JsSdk/2.0 NetType/WIFI Channel/App
 * Store ByteLocale/en Region/US ...`, so the original /bytedance/ sniff
 * (copied from `isTikTokBrowser` in apps/web/pages/tiktok/open-in-browser.js)
 * silently missed all of them. "musical_ly" is TikTok's app UA token on iOS
 * (`musical_ly_<version>`) and Android (`AppName/musical_ly`); "bytedance"
 * is kept for older WebViews (`BytedanceWebview/...`). Both tokens are
 * unique to TikTok — no other browser's UA contains either.
 *
 * Covers the /tiktok/[slug] x TikTok WebView x GTM family: LADDER-WEBSITE-8HW
 * (consentManager), 8HX (setting 'event'), 8GT / 8K8 (addEventListener),
 * 8EJ (consent), 8NZ / 8P0 (twq — a GTM tag calling the X/Twitter pixel
 * that never loaded), and the related 8DB / 8DC / 8FA variants.
 */
const TIKTOK_UA_TOKENS = /bytedance|musical_ly/i;

function isTikTokAnalyticsError(event: Event): boolean {
  if (
    typeof navigator === "undefined" ||
    !TIKTOK_UA_TOKENS.test(navigator.userAgent)
  ) {
    return false;
  }
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames || !Array.isArray(frames)) return false;
  const hasAnalyticsFrame = frames.some((frame: StackFrame) => {
    const path = frame.abs_path ?? frame.filename ?? frame.module ?? "";
    return ANALYTICS_SCRIPT_FRAME.test(path);
  });
  if (!hasAnalyticsFrame) return false;
  // Mixed stacks still report: first-party code calling into an analytics
  // bundle (window.analytics.reset() → a Segment destination) is our bug
  // surface, not injected-tag noise. But "any first-party frame" is the
  // wrong test for that: the Sentry SDK's own instrumentation lives in our
  // chunks and brackets otherwise-pure tag stacks — the wrapped
  // setTimeout/listener that *invoked* the tag is the outermost frame, and
  // a wrapped fetch/XHR the tag *called* is the innermost. The real
  // LADDER-WEBSITE-8NZ stack is exactly `_next/static/chunks/….js (n)`
  // followed by gtm.js × N, so counting end frames as ours makes this
  // filter a no-op for its headline targets. First-party evidence must
  // therefore sit strictly inside the stack, where a genuine first-party
  // caller (our page code between the task root and the analytics bundle)
  // actually appears.
  const hasInteriorFirstPartyFrame = frames.some(
    (frame: StackFrame, i: number) => {
      if (i === 0 || i === frames.length - 1) return false;
      const path = frame.abs_path ?? frame.filename ?? frame.module ?? "";
      return FIRST_PARTY_SCRIPT_FRAME.test(path);
    }
  );
  return !hasInteriorFirstPartyFrame;
}

/**
 * Stack-frame paths that belong to our own JavaScript bundle. A genuine
 * first-party infinite recursion carries at least one of these frames even
 * without a source map.
 */
const FIRST_PARTY_SCRIPT_FRAME = /_next\/static|\/chunks\//i;

/**
 * Stack-frame paths that carry no script origin at all. `<anonymous>` is what
 * V8 reports for code run through `eval` / `new Function`; JavaScriptCore
 * reports `[native code]` for the same and for host functions; a parser that
 * could not extract a location leaves the path empty. Nothing in this repo
 * evals code — Next serves every first-party script from `_next/static/`, and
 * our inline `<script>` blocks resolve to the page URL, not to `<anonymous>` —
 * so a throw whose frame has one of these paths came from something injected
 * into the page client-side.
 */
const UNRESOLVABLE_FRAME_PATH = /^(?:<anonymous>|\[native code\])?$/;

/**
 * TikTok's in-app browser runs tag-manager snippets that create their own
 * XMLHttpRequest and read `.status` on something null in the readystate
 * callback. The snippet is eval'd, so the throwing frame is `<anonymous>` —
 * no script URL for `isTikTokAnalyticsError` to key on — and its function name
 * is a minified `c`, so `INJECTED_BRIDGE_FUNCTIONS` can't name it the way it
 * names the Curalate poller above. The message ("Cannot read properties of
 * null (reading 'status')") is far too generic to regex on.
 *
 * The stack shape is the reliable signal. All 221 events of LADDER-WEBSITE-8RB
 * (218 users in 7 days, 100% TikTok WebView on iOS and Android, plus the 8TW
 * sibling) carry exactly two frames: the Sentry SDK's XHR-handler wrapper —
 * which lives in one of our chunks — as the outermost frame, and `<anonymous>`
 * as the throw. Conjunction filter, mirroring the siblings: the live UA is
 * TikTok's in-app browser AND the throwing frame has no resolvable path AND
 * every frame above it is either unresolvable too or, for the outermost frame
 * only, the SDK's XHR wrapper in our bundle — recognised by its function name,
 * `XMLHttpRequest.<minified>`, not merely by living in one of our chunks. That
 * distinction matters because V8 also reports native builtins as
 * `<anonymous>` (`JSON.parse (<anonymous>)`) and JavaScriptCore as
 * `[native code]`: a first-party handler whose only JS frame is ours and
 * whose throw is a builtin would otherwise be dropped for TikTok users. A
 * first-party frame anywhere inside the stack (our code calling into the
 * snippet), a throw from any script with a real URL, any other browser, or a
 * stack with no frames at all still reports.
 *
 * The same snippets also run from timers (`VWO.visitor is not a function`),
 * where the outermost frame is the SDK's setTimeout wrap and its minified name
 * says nothing. The `browserApiErrors` mechanism the SDK stamps on everything
 * its wraps catch identifies that frame instead; a first-party callback still
 * puts its own frame inside the stack and reports.
 */
const SDK_XHR_WRAPPER_FUNCTION = /^XMLHttpRequest\./;
const SDK_WRAPPED_CALLBACK_MECHANISM = /^auto\.browser\.browserapierrors\./;
function isTikTokAnonymousScriptError(event: Event): boolean {
  if (
    typeof navigator === "undefined" ||
    !TIKTOK_UA_TOKENS.test(navigator.userAgent)
  ) {
    return false;
  }
  const exception = event.exception?.values?.[0];
  const frames = exception?.stacktrace?.frames;
  // No resolvable stack at all → fail open (report), matching the siblings.
  if (!frames || !Array.isArray(frames) || frames.length === 0) return false;
  const pathOf = (frame: StackFrame) =>
    frame.abs_path ?? frame.filename ?? frame.module ?? "";
  // Frames are outermost-first; the throw is the last one.
  if (!UNRESOLVABLE_FRAME_PATH.test(pathOf(frames[frames.length - 1]))) {
    return false;
  }
  const caughtBySdkWrap = SDK_WRAPPED_CALLBACK_MECHANISM.test(
    exception?.mechanism?.type ?? ""
  );
  for (let i = 0; i < frames.length - 1; i++) {
    const path = pathOf(frames[i]);
    if (UNRESOLVABLE_FRAME_PATH.test(path)) continue;
    if (
      i === 0 &&
      FIRST_PARTY_SCRIPT_FRAME.test(path) &&
      (caughtBySdkWrap ||
        SDK_XHR_WRAPPER_FUNCTION.test(frames[i].function ?? ""))
    ) {
      continue;
    }
    return false;
  }
  return true;
}

/**
 * Chrome for iOS (browser.name "Chrome Mobile iOS") injects its own content
 * scripts into every page it renders — the __gCrWeb bridge, translate, password
 * autofill. On Chrome iOS 148/149 one of those injected scripts hits infinite
 * recursion and throws `RangeError: Maximum call stack size exceeded` straight
 * to the global onerror handler. The stack has no first-party frames: every
 * frame is an anonymous `undefined:LINE:COL` from the injected script, never one
 * of our `_next/static` bundles. It's the Chrome-iOS-app sibling of the TikTok
 * WebView noise above — the ad link opened in Chrome rather than TikTok's IAB.
 *
 * We filter on the conjunction (mirroring the IAB / TikTok filters): browser is
 * Chrome Mobile iOS AND the message is the max-call-stack RangeError AND no
 * stack frame points at our own bundle. A real app-code infinite recursion
 * still carries `_next/static` frames and is NOT caught, so genuine bugs — and
 * every other browser — pass through.
 *
 * LADDER-WEBSITE-73M, ~5.5k events / 1.2k users since 2025-06-27, fires on
 * paid-ad landing pages (/tiktok/[slug], /meta/[slug], /quiz, ...), not
 * user-visible (page renders; error only reaches window.onerror).
 */
function isChromeIosStackOverflow(event: Event): boolean {
  // Detect Chrome-on-iOS from the live user agent, NOT event.contexts.browser:
  // that context is derived at Sentry ingest from the UA header and does not
  // exist client-side in beforeSend — guarding on it makes the filter a
  // silent no-op (verified against @sentry/browser 10.x internals).
  if (typeof navigator === "undefined" || !/CriOS/.test(navigator.userAgent)) {
    return false;
  }
  const value = event.exception?.values?.[0]?.value ?? "";
  if (!/Maximum call stack size exceeded/i.test(value)) return false;
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  // No resolvable stack at all → fail open (report), matching the sibling
  // filters: we only suppress when frames exist and none point at our bundle.
  if (!frames || !Array.isArray(frames) || frames.length === 0) return false;
  const hasFirstPartyFrame = frames.some((frame: StackFrame) => {
    // Do NOT treat frame.in_app as first-party evidence: the browser SDK
    // marks every parsed frame in_app client-side, which would make this
    // check always-true and the filter dead. The filename test is what
    // distinguishes our bundles (rewriteFrames runs before beforeSend, so
    // first-party frames read app:///_next/static/...).
    const path = frame.abs_path ?? frame.filename ?? frame.module ?? "";
    return FIRST_PARTY_SCRIPT_FRAME.test(path);
  });
  return !hasFirstPartyFrame;
}

/**
 * Minified scripts injected by ad landing-page surfaces (GTM custom tags,
 * in-app-browser bridges) throw bare string tokens — `throw "ga"` reaches
 * window.onerror as just "ga" and Sentry renders it `Error: ga`. Seen as the
 * Error: fa / Aa / ga family (LADDER-WEBSITE-8JF / 8HC / 8PJ), all on
 * /tiktok/[slug] and /quiz in mobile in-app browsers and Chrome iOS. The
 * /quiz document ships as a single minified line, so the reported frame
 * (`app:///quiz:415:45`) can only come from a browser-side rewritten/injected
 * script — grep confirms no first-party `throw "<token>"` exists.
 *
 * Conjunction filter (mirroring the siblings above): the exception value is a
 * 1–3 letter opaque token AND no stack frame points at our own bundle. A
 * hypothetical first-party thrown string keeps its `_next/static` frame and
 * still reports.
 */
function isOpaqueMinifiedThrow(event: Event): boolean {
  const value = event.exception?.values?.[0]?.value ?? "";
  if (!/^[A-Za-z]{1,3}$/.test(value)) return false;
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  // These onerror string-throws carry either no frames or a single
  // document-level frame (app:///quiz:LINE:COL) — neither is first-party.
  if (!frames || !Array.isArray(frames) || frames.length === 0) return true;
  return !frames.some((frame: StackFrame) => {
    const path = frame.abs_path ?? frame.filename ?? frame.module ?? "";
    return FIRST_PARTY_SCRIPT_FRAME.test(path);
  });
}

/**
 * Stack-frame path of an injected script that reports itself as
 * `/executors/<n>.js`. No such route, public asset, or rewrite exists in this
 * monorepo — `apps/web/public/` has no `executors/` directory and Next serves
 * its own bundles from `_next/static/chunks/` — so a frame at this path can
 * only come from code injected into the page client-side. (`app:///` is just
 * what rewriteFrames prefixes onto a same-origin-looking path; it is not
 * evidence that we served the file.)
 */
const INJECTED_EXECUTOR_FRAME = /\/executors\/\d+\.js/i;

/**
 * The injected `executors/<n>.js` script hooks `XMLHttpRequest`
 * (`XMLHttpRequest.onreadystatechange` is the throwing frame) and dereferences
 * an undefined object, producing `TypeError: Cannot read properties of
 * undefined (reading 'M_ID')` straight to onunhandledrejection. `M_ID` is not a
 * symbol in this repo — the only greps are substrings of TEAM_ID / PROGRAM_ID.
 * Covers LADDER-WEBSITE-8QT (346 events / 21 users, from /) and its /app
 * sibling 8S5 (179 / 11); together the largest client-side noise source in the
 * web project. The vendor is unidentified — the path is not attributable to a
 * publicly documented extension — which is exactly why this filter keys on the
 * path rather than on the (very generic) message.
 *
 * Conjunction filter, matching the siblings above and deliberately stricter
 * than `isInjectedBridgeError`: at least one frame is an injected-executor
 * frame AND no frame points at our own bundle, so a first-party caller of the
 * hooked XHR keeps reporting.
 *
 * One `_next/static` frame is not ours, though: when the browser dispatches the
 * injected `onreadystatechange`, it goes through the Sentry SDK's
 * browserApiErrors `wrap()`, which lives in our chunks and shows up as the
 * outermost frame with an `XMLHttpRequest.` receiver. Every surviving 8S5
 * event has exactly `[chunk XMLHttpRequest.n] → [executors/200.js] × 2`, so
 * that dispatcher frame is not counted as first-party (same reasoning as the
 * end-frame exclusion in `isTikTokAnalyticsError`).
 */
function isSdkXhrDispatchFrame(frames: StackFrame[], i: number): boolean {
  if (i !== 0 || frames.length < 2) return false;
  if (!/^XMLHttpRequest\./.test(frames[0].function ?? "")) return false;
  const next = frames[1];
  return INJECTED_EXECUTOR_FRAME.test(next.abs_path ?? next.filename ?? next.module ?? "");
}

function isInjectedExecutorError(event: Event): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  // No resolvable stack → fail open (report): the path is the only signal.
  if (!frames || !Array.isArray(frames) || frames.length === 0) return false;
  let sawExecutorFrame = false;
  for (const [i, frame] of (frames as StackFrame[]).entries()) {
    const path = frame.abs_path ?? frame.filename ?? frame.module ?? "";
    if (FIRST_PARTY_SCRIPT_FRAME.test(path) && !isSdkXhrDispatchFrame(frames, i)) return false;
    if (INJECTED_EXECUTOR_FRAME.test(path)) sawExecutorFrame = true;
  }
  return sawExecutorFrame;
}

/**
 * A browser extension's own script. Safari app extensions are reported by their
 * on-disk bundle path (`/Applications/<App>.app/…/<Name>.safariextension/…`),
 * which rewriteFrames then prefixes with `app:///` like any same-origin path;
 * the other engines use an extension URL scheme. PayPal Honey's
 * `Honey.safariextension/h0.js` throws `UnavailableError` as an unhandled
 * rejection on whatever page is open — LADDER-WEBSITE-8G1, 68 events / 27
 * users over 13 routes, every one with that single frame.
 *
 * Same conjunction as `isInjectedExecutorError`: an extension frame AND no
 * frame in our bundles, so a first-party caller still reports.
 */
const BROWSER_EXTENSION_FRAME =
  /\.safariextension\/|^(?:safari-(?:web-)?extension|chrome-extension|moz-extension):\/\//i;

function isBrowserExtensionError(event: Event): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames || !Array.isArray(frames) || frames.length === 0) return false;
  let sawExtensionFrame = false;
  for (const frame of frames as StackFrame[]) {
    const path = frame.abs_path ?? frame.filename ?? frame.module ?? "";
    if (FIRST_PARTY_SCRIPT_FRAME.test(path)) return false;
    if (BROWSER_EXTENSION_FRAME.test(path)) sawExtensionFrame = true;
  }
  return sawExtensionFrame;
}

/**
 * A script injected at the site root as `/pixel.js`, with helpers under
 * `/src/` (`mediafilter.generic-wrapper.min.js`, `setup.js`). No app here
 * serves either path. It shows up in Safari, Chrome and Firefox from the same
 * networks, so it is injected upstream of the browser (a content filter), not
 * by an extension, and `BROWSER_EXTENSION_FRAME` never matches it.
 * LADDER-WEBSITE-8ZC (DataCloneError from its MutationObserver) and 8A6
 * (obfuscated `navigator[r(N)] is not a function`).
 *
 * Same conjunction as the siblings: an injected frame AND none of our bundles.
 */
const INJECTED_PIXEL_FRAME = /^app:\/\/\/(?:pixel\.js|src\/mediafilter\.[\w.-]*\.js)(?:[?#:]|$)/i;

function isInjectedPixelScriptError(event: Event): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames || !Array.isArray(frames) || frames.length === 0) return false;
  let sawInjectedFrame = false;
  for (const frame of frames as StackFrame[]) {
    const path = frame.abs_path ?? frame.filename ?? frame.module ?? "";
    if (FIRST_PARTY_SCRIPT_FRAME.test(path)) return false;
    if (INJECTED_PIXEL_FRAME.test(path)) sawInjectedFrame = true;
  }
  return sawInjectedFrame;
}

/**
 * Facebook's and Instagram's Android in-app browsers report the scripts they
 * inject under a private scheme with a host — `app://browser_declutter`,
 * `app://navigation_performance_logger_android`. rewriteFrames turns every
 * path of ours into `app:///…` (empty host), so a frame with a non-empty
 * `app://` host can only be Meta's. LADDER-WEBSITE-8YJ (`SyntaxError:
 * Unexpected end of input` from browser_declutter) and 8DM (a third
 * `Error invoking postMessage` wording the Java-bridge pattern above misses).
 *
 * Same conjunction as the siblings: an injected frame AND none of our bundles.
 */
const META_IAB_INJECTED_FRAME = /^app:\/\/[^/]/i;

function isMetaIabInjectedScriptError(event: Event): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames || !Array.isArray(frames) || frames.length === 0) return false;
  let sawInjectedFrame = false;
  for (const frame of frames as StackFrame[]) {
    const path = frame.abs_path ?? frame.filename ?? frame.module ?? "";
    if (FIRST_PARTY_SCRIPT_FRAME.test(path)) return false;
    if (META_IAB_INJECTED_FRAME.test(path)) sawInjectedFrame = true;
  }
  return sawInjectedFrame;
}

/**
 * Check if an error message matches any of the logged-only patterns
 */
function matchesLoggedPattern(message: string | undefined): boolean {
  if (!message) return false;
  return LOGGED_ERROR_PATTERNS.some(pattern => pattern.test(message));
}

/**
 * Extract error message from various error formats
 */
function getErrorMessage(event: Event): string | undefined {
  // Check exception values
  if (event.exception?.values?.length) {
    for (const exception of event.exception.values) {
      if (exception.value) return exception.value;
      if (exception.type) return exception.type;
    }
  }
  
  // Check error message
  if (event.message) return event.message;
  
  // Check breadcrumbs for recent errors
  if (event.breadcrumbs?.length) {
    const errorBreadcrumb = event.breadcrumbs
      .filter((b: Breadcrumb) => b.category === 'error' || b.level === 'error')
      .pop();
    if (errorBreadcrumb?.message) return errorBreadcrumb.message;
  }
  
  return undefined;
}

/**
 * Check if error originates from iOS WebView
 */
function isIOSWebViewError(event: Event): boolean {
  const userAgent = event.request?.headers?.['User-Agent'] || 
                   (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  
  // Check for iOS WebView indicators
  const isIOSWebView = /iPhone|iPad|iPod/.test(userAgent) && 
                      (/AppleWebKit/.test(userAgent) && !/Safari/.test(userAgent));
  
  // Check for specific iOS error indicators in the event
  const hasIOSErrorIndicators = event.tags?.os === 'iOS' ||
                               event.contexts?.os?.name === 'iOS' ||
                               event.platform === 'javascript' && isIOSWebView;
  
  return hasIOSErrorIndicators;
}

/**
 * Check whether an event was deliberately captured from a user-visible failure.
 * Such events bypass message-pattern filtering — if a user saw something break,
 * we always want it in Sentry, even if the underlying message looks like a
 * generic network error.
 */
function isUserVisibleCapture(event: Event): boolean {
  const tag = event.tags?.user_visible;
  return tag === true || tag === "true";
}

// How a response-less request failure words itself, per engine: axios turns
// XHR `onerror` into "Network Error" and `onabort` into "Request aborted";
// `fetch` rejects with a bare TypeError whose message is the engine's own
// ("Failed to fetch" on Blink, "Load failed" on WebKit, "NetworkError when
// attempting to fetch resource." on Gecko).
// Most of these also appear in FILTERED_ERROR_PATTERNS (~L38–88), which is read
// later and off the event message; keep the two in step when adding a wording.
const RESPONSELESS_FAILURE_MESSAGES = [
  /network error/i,
  /request aborted/i,
  /failed to fetch/i,
  // Anchored: WebKit's message is exactly "Load failed", and unanchored this
  // would also swallow things like "Script load failed" that are not this.
  /^load failed\.?$/i,
  /networkerror when attempting to fetch/i,
  /the operation was aborted/i,
];

/**
 * Check whether the thrown error is a request that never got a response —
 * a transport failure or a cancellation, as opposed to a 4xx/5xx or a bug.
 *
 * Read off `hint.originalException` rather than the event, because the event
 * only carries the message and that alone can't tell a cancelled axios read
 * from a real one: a 500 and a cancellation share the `AxiosError` type.
 */
function isResponselessRequestFailure(hint: EventHint): boolean {
  const error: any = hint?.originalException;
  if (!error || typeof error !== "object") return false;
  // axios attaches `response` iff the server answered, so its presence rules
  // out a cancellation whatever the message says.
  if (error.isAxiosError === true && error.response !== undefined) return false;
  if (error.name === "AbortError") return true;
  // axios never throws `AbortError`: an AbortController cancellation comes back
  // as its own `CanceledError`, whose message is "canceled" and matches none of
  // the wordings below. Its `code` is the stable signal — wording- and
  // localization-proof. `ECONNABORTED` (axios's timeout) is deliberately absent:
  // a timeout failed on its own merits, unload or not.
  if (error.code === "ERR_CANCELED" || error.code === "ERR_NETWORK") return true;
  return (
    typeof error.message === "string" &&
    RESPONSELESS_FAILURE_MESSAGES.some((pattern) => pattern.test(error.message))
  );
}

/**
 * Enhanced beforeSend hook for Sentry with comprehensive error filtering
 */
export function sentryBeforeSend(event: Event, hint: EventHint): Event | null {
  // A read the browser cancelled because the document is going away rendered
  // nothing and told the user nothing, so it is noise even when the reporter
  // tagged it user_visible — and ~25 call sites carry that tag. Deciding it
  // here rather than per-reporter covers all of them at once, including the
  // ones not written yet (LADDER-WEBSITE-8T8).
  if (isResponselessRequestFailure(hint) && isPageUnloading()) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered request cancelled by navigation:', getErrorMessage(event));
    }
    return null;
  }

  // Never drop events that were deliberately captured from a user-visible failure.
  if (isUserVisibleCapture(event)) {
    return event;
  }

  const errorMessage = getErrorMessage(event);

  // Filter out known non-critical errors
  if (matchesFilteredPattern(errorMessage)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered error:', errorMessage);
    }
    return null;
  }

  // Filter errors whose stack is rooted in IAB-injected bridge JS,
  // even when the message looks like a real bug.
  if (isInjectedBridgeError(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered IAB bridge error:', errorMessage);
    }
    return null;
  }

  // Filter ReferenceErrors from Facebook/Instagram IAB-injected scripts.
  // Variable names vary by IAB version so we filter on live UA rather
  // than message pattern.
  if (isIabInjectedReferenceError(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered IAB reference error:', errorMessage);
    }
    return null;
  }

  // Filter GTM/Segment/Optimize analytics errors fired inside TikTok's in-app
  // browser. Third-party WebView noise — generic-looking TypeErrors with no
  // first-party frames; keyed on live UA + an analytics frame so real bugs
  // (and other browsers) pass through.
  if (isTikTokAnalyticsError(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered TikTok analytics error:', errorMessage);
    }
    return null;
  }

  // Filter throws from eval'd (`<anonymous>`) snippets inside TikTok's in-app
  // browser whose only first-party frame is the SDK wrapper that invoked them.
  if (isTikTokAnonymousScriptError(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered TikTok anonymous-script error:', errorMessage);
    }
    return null;
  }

  // Filter `Maximum call stack size exceeded` thrown by Chrome-for-iOS injected
  // content scripts (no first-party frames). Real app-code recursions keep their
  // `_next/static` frames and pass through.
  if (isChromeIosStackOverflow(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered Chrome iOS stack-overflow:', errorMessage);
    }
    return null;
  }

  // Filter bare 1–3 letter string throws (`Error: ga` / fa / Aa) from
  // minified injected scripts on landing pages — no first-party frames.
  if (isOpaqueMinifiedThrow(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered opaque minified throw:', errorMessage);
    }
    return null;
  }

  // Filter throws from the injected `executors/<n>.js` XHR hook — a path this
  // repo never serves. Stacks that also carry first-party code still report.
  if (isInjectedExecutorError(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered injected executor error:', errorMessage);
    }
    return null;
  }

  // Filter throws whose stack is a browser extension's script and none of ours.
  if (isBrowserExtensionError(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered browser extension error:', errorMessage);
    }
    return null;
  }

  // Filter throws from the root-level `pixel.js` script a content filter injects.
  if (isInjectedPixelScriptError(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered injected pixel.js error:', errorMessage);
    }
    return null;
  }

  // Filter throws from scripts Meta's Android in-app browsers inject as `app://<name>`.
  if (isMetaIabInjectedScriptError(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered Meta IAB injected-script error:', errorMessage);
    }
    return null;
  }

  // Filter rejections the Firebase SDK leaves unobserved (Remote Config
  // storage-open/storage-get, Installations app-offline). A first-party capture of the same
  // error still reports.
  if (isFirebaseUnobservedSdkRejection(event)) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Sentry] Filtered unobserved Firebase SDK rejection:', errorMessage);
    }
    return null;
  }

  // Log but don't send certain errors
  if (matchesLoggedPattern(errorMessage)) {
    if (typeof console !== 'undefined') {
      console.warn('[Sentry] Logged but not sent:', errorMessage, {
        url: event.request?.url,
        user: event.user,
        tags: event.tags,
      });
    }
    return null;
  }
  
  // Additional filtering for iOS WebView specific errors
  if (isIOSWebViewError(event)) {
    // Be more aggressive about filtering iOS errors
    const iosFilterPatterns = [
      /undefined is not an object/i,
      /null is not an object/i,
      /cannot read prop/i,
      /cannot access/i,
    ];
    
    if (errorMessage && iosFilterPatterns.some(pattern => pattern.test(errorMessage))) {
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[Sentry] Filtered iOS WebView error:', errorMessage);
      }
      return null;
    }
  }
  
  // Add additional context for errors that pass through
  if (event.extra) {
    event.extra.filtered = false;
    event.extra.errorMessage = errorMessage;
  } else {
    event.extra = {
      filtered: false,
      errorMessage,
    };
  }
  
  return event;
}

/**
 * Optional: More aggressive filtering for specific environments
 */
export function sentryBeforeSendAggressive(event: Event, hint: EventHint): Event | null {
  // First apply standard filtering
  const standardResult = sentryBeforeSend(event, hint);
  if (!standardResult) return null;
  
  // Additional aggressive filters
  const errorMessage = getErrorMessage(event);
  
  // Filter out all errors without meaningful stack traces
  if (!event.exception?.values?.some((ex: Exception) => ex.stacktrace?.frames?.length)) {
    if (errorMessage && !errorMessage.includes('TypeError') && !errorMessage.includes('ReferenceError')) {
      return null;
    }
  }
  
  // Filter out errors from third-party domains
  const errorUrl = event.request?.url || '';
  const trustedDomains = [
    'joinladder.com',
    'ltdev.io',
    'localhost',
  ];
  
  if (!trustedDomains.some(domain => errorUrl.includes(domain))) {
    // Check if error originated from our code
    const hasOurCode = event.exception?.values?.some((ex: Exception) => 
      ex.stacktrace?.frames?.some((frame: StackFrame) => 
        trustedDomains.some(domain => frame.filename?.includes(domain))
      )
    );
    
    if (!hasOurCode) {
      return null;
    }
  }
  
  return standardResult;
}